/**
 * A planned care-manager visit on the Appointments card (0240): who, when
 * and its line, until its date. A visit is a plan; after its date the card
 * no longer shows it (the caller filters by the practice's today). Remove
 * shows for a seat that keeps the record; the server decides whose visit
 * it may remove.
 */
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { TextAction } from '@/components/workspace/TextAction';
import { useTenant } from '@/lib/auth';
import { canWriteClinicalRecord } from '@/lib/capabilities';
import { careVisitsApi, refreshAfterCareVisitChange, type CareVisit } from '@/lib/careVisitsApi';
import { pageWriteText, serverSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { fmtTime, proseDate } from '@/lib/proseDate';

export function CareVisitRow({ visit, clientId }: { visit: CareVisit; clientId: string }): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  const queryClient = useQueryClient();
  const contact = useSupportContact({ enabled: false });
  const remove = useMutation({
    mutationFn: (v: { clientId: string; visitId: string }) => careVisitsApi.remove(tenantSlug, v.clientId, v.visitId),
    onSuccess: (_r, v) => refreshAfterCareVisitChange(queryClient, tenantSlug, v.clientId),
  });
  const when = `${proseDate(visit.scheduled_for)}${visit.scheduled_time ? ` at ${fmtTime(visit.scheduled_time)}` : ''}`;
  // One line, in the appointment rows' own frame: who, when, the line.
  return (
    <li
      data-testid={`care-visit-${visit.id}`}
      className="group flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-base border border-sage-800/[6%] px-3 py-2 text-sm"
    >
      {/* One line where it fits: the name and the date never truncate, the
          note does; on a phone the line wraps rather than cutting the date. */}
      <span className="shrink-0 font-semibold text-sage-800">Visit: {visit.care_manager_name}</span>
      <span className="shrink-0 whitespace-nowrap text-ink-soft">{when}</span>
      {visit.note !== null && (
        <span className="min-w-0 flex-[1_1_8rem] truncate text-sage-800" title={visit.note}>
          {visit.note}
        </span>
      )}
      {canWrite && (
        <TextAction
          className="ml-auto shrink-0"
          data-testid={`care-visit-remove-${visit.id}`}
          disabled={remove.isPending}
          onClick={() => remove.mutate({ clientId, visitId: visit.id })}
        >
          Remove
        </TextAction>
      )}
      {remove.isError && (
        // A seat that reads the record without keeping it reads the
        // server's own sentence (care_record_read_only).
        <p role="alert" data-testid={`care-visit-remove-error-${visit.id}`} className="basis-full text-xs text-pepper-deep">
          {serverSentence(remove.error, 'record_edits') ?? pageWriteText(remove.error, contact)}
        </p>
      )}
    </li>
  );
}
